import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Edit, Eye, Clipboard, Users, AlertCircle, CheckCircle } from 'lucide-react';
import Modal from '../components/Common/Modal';
import { toast } from 'react-toastify';
import CustomSelect from '../components/Common/CustomSelect';
import {
  MDTCase,
  getMDTCasesByPatient,
  createMDTCase,
  updateMDTCase,
  deleteMDTCase,
  validateCaseCompleteness,
  CaseCompleteness,
} from '../services/mdtCaseService';

interface CasesTabProps {
  patientId: string;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

const caseStatuses = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'mdt_ready', label: 'MDT Ready' },
  { value: 'completed', label: 'Completed' },
  { value: 'approved', label: 'Approved' },
];

const performanceStatuses = [
  'ECOG 0 - Fully active',
  'ECOG 1 - Restricted',
  'ECOG 2 - Ambulatory',
  'ECOG 3 - Limited selfcare',
  'ECOG 4 - Completely disabled',
];

const CasesTab: React.FC<CasesTabProps> = ({
  patientId,
  canCreate,
  canUpdate,
  canDelete,
}) => {
  const [cases, setCases] = useState<MDTCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState<MDTCase | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [completeness, setCompleteness] = useState<CaseCompleteness | null>(null);

  const [formTitle, setFormTitle] = useState('');
  const [formDiagnosis, setFormDiagnosis] = useState('');
  const [formDiseaseStatus, setFormDiseaseStatus] = useState('');
  const [formRelapseDetails, setFormRelapseDetails] = useState('');
  const [formRelapseDate, setFormRelapseDate] = useState('');
  const [formPerformanceStatus, setFormPerformanceStatus] = useState('');
  const [formTreatmentIntent, setFormTreatmentIntent] = useState('');
  const [formDiseaseSite, setFormDiseaseSite] = useState('');
  const [formPreviousTreatment, setFormPreviousTreatment] = useState('');
  const [formComorbidities, setFormComorbidities] = useState('');
  const [formInvestigationSummary, setFormInvestigationSummary] = useState('');
  const [formStatus, setFormStatus] = useState('draft');

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMDTCasesByPatient(patientId);
      setCases(data);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const resetForm = () => {
    setFormTitle('');
    setFormDiagnosis('');
    setFormDiseaseStatus('');
    setFormRelapseDetails('');
    setFormRelapseDate('');
    setFormPerformanceStatus('');
    setFormTreatmentIntent('');
    setFormDiseaseSite('');
    setFormPreviousTreatment('');
    setFormComorbidities('');
    setFormInvestigationSummary('');
    setFormStatus('draft');
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle) {
      toast.error('Case title is required');
      return;
    }
    try {
      await createMDTCase({
        patient_id: patientId,
        title: formTitle,
        diagnosis: formDiagnosis || undefined,
        disease_status: formDiseaseStatus || undefined,
        relapse_details: formRelapseDetails || undefined,
        relapse_date: formRelapseDate || undefined,
        performance_status: formPerformanceStatus || undefined,
        treatment_intent: formTreatmentIntent || undefined,
        disease_site: formDiseaseSite || undefined,
        previous_treatment_summary: formPreviousTreatment || undefined,
        comorbidities: formComorbidities || undefined,
        investigation_summary: formInvestigationSummary || undefined,
      });
      toast.success('Case created successfully');
      setShowAddModal(false);
      resetForm();
      fetchCases();
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error('Failed to create case');
    }
  };

  const handleEdit = (mdtCase: MDTCase) => {
    setSelectedCase(mdtCase);
    setFormTitle(mdtCase.title);
    setFormDiagnosis(mdtCase.diagnosis || '');
    setFormDiseaseStatus(mdtCase.disease_status || '');
    setFormRelapseDetails(mdtCase.relapse_details || '');
    setFormRelapseDate(mdtCase.relapse_date || '');
    setFormPerformanceStatus(mdtCase.performance_status || '');
    setFormTreatmentIntent(mdtCase.treatment_intent || '');
    setFormDiseaseSite(mdtCase.disease_site || '');
    setFormPreviousTreatment(mdtCase.previous_treatment_summary || '');
    setFormComorbidities(mdtCase.comorbidities || '');
    setFormInvestigationSummary(mdtCase.investigation_summary || '');
    setFormStatus(mdtCase.status);
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCase) return;
    try {
      await updateMDTCase(selectedCase.id, {
        title: formTitle,
        status: formStatus,
        diagnosis: formDiagnosis || undefined,
        disease_status: formDiseaseStatus || undefined,
        relapse_details: formRelapseDetails || undefined,
        relapse_date: formRelapseDate || undefined,
        performance_status: formPerformanceStatus || undefined,
        treatment_intent: formTreatmentIntent || undefined,
        disease_site: formDiseaseSite || undefined,
        previous_treatment_summary: formPreviousTreatment || undefined,
        comorbidities: formComorbidities || undefined,
        investigation_summary: formInvestigationSummary || undefined,
      });
      toast.success('Case updated successfully');
      setShowEditModal(false);
      setSelectedCase(null);
      resetForm();
      fetchCases();
    } catch (error) {
      console.error('Error updating case:', error);
      toast.error('Failed to update case');
    }
  };

  const handleDelete = async () => {
    if (!selectedCaseId) return;
    try {
      await deleteMDTCase(selectedCaseId);
      toast.success('Case deleted');
      setDeleteModalOpen(false);
      setSelectedCaseId(null);
      fetchCases();
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Failed to delete case');
    }
  };

  const handleValidate = async (caseId: number) => {
    try {
      const result = await validateCaseCompleteness(caseId);
      setCompleteness(result);
      if (result.is_complete) {
        toast.success('Case is complete and ready for MDT report generation');
      } else {
        toast.warning(`Case is incomplete: ${result.missing_fields.join(', ')}`);
      }
    } catch (error) {
      console.error('Error validating case:', error);
      toast.error('Failed to validate case');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-neutral-100 text-neutral-800';
      case 'open':
        return 'bg-primary-100 text-primary-800';
      case 'under_review':
        return 'bg-warning-50 text-warning-600';
      case 'mdt_ready':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-success-50 text-success-600';
      case 'approved':
        return 'bg-success-50 text-success-600';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getReviewStatusIcon = (status: string) => {
    return status === 'completed' ? (
      <CheckCircle className="h-4 w-4 text-success-500" />
    ) : (
      <AlertCircle className="h-4 w-4 text-neutral-400" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-neutral-900">MDT Cases</h3>
        {canCreate && (
          <button onClick={() => setShowAddModal(true)} className="btn-primary flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Create Case</span>
          </button>
        )}
      </div>

      {cases.length === 0 ? (
        <div className="card text-center py-8">
          <Clipboard className="h-10 w-10 text-neutral-400 mx-auto mb-3" />
          <p className="text-neutral-600">No MDT cases found for this patient.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {cases.map((mdtCase) => (
            <div key={mdtCase.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h4 className="font-medium text-neutral-900">{mdtCase.title}</h4>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(mdtCase.status)}`}>
                      {mdtCase.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-600 space-y-1">
                    {mdtCase.diagnosis && <p>Diagnosis: {mdtCase.diagnosis}</p>}
                    {mdtCase.disease_status && <p>Disease Status: {mdtCase.disease_status}</p>}
                    {mdtCase.treatment_intent && <p>Treatment Intent: {mdtCase.treatment_intent}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-neutral-500">
                    <div className="flex items-center space-x-1">
                      <Users className="h-3 w-3" />
                      <span>Review Status:</span>
                    </div>
                    <div className="flex items-center space-x-1" title="Oncologist">
                      {getReviewStatusIcon(mdtCase.oncologist_review_status)}
                      <span>Onc</span>
                    </div>
                    <div className="flex items-center space-x-1" title="Histopathologist">
                      {getReviewStatusIcon(mdtCase.histopathologist_review_status)}
                      <span>Histo</span>
                    </div>
                    <div className="flex items-center space-x-1" title="Radiologist">
                      {getReviewStatusIcon(mdtCase.radiologist_review_status)}
                      <span>Rad</span>
                    </div>
                    <div className="flex items-center space-x-1" title="Supportive Care">
                      {getReviewStatusIcon(mdtCase.supportive_care_review_status)}
                      <span>SC</span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => { setSelectedCase(mdtCase); setShowDetailModal(true); handleValidate(mdtCase.id); }}
                    className="text-info-600 hover:text-info-900"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {canUpdate && (
                    <button onClick={() => handleEdit(mdtCase)} className="text-primary-600 hover:text-primary-900" title="Edit">
                      <Edit className="h-4 w-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => { setSelectedCaseId(mdtCase.id); setDeleteModalOpen(true); }}
                      className="text-danger-600 hover:text-danger-800"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      <Modal title="Create MDT Case" isOpen={showAddModal} onClose={() => { setShowAddModal(false); resetForm(); }} widthClassName="max-w-4xl">
        <form onSubmit={handleAdd} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Case Title *</label>
              <input value={formTitle} onChange={(e) => setFormTitle(e.target.value)} className="input-field mt-1" placeholder="e.g., DLBCL - Initial Assessment" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Diagnosis</label>
              <input value={formDiagnosis} onChange={(e) => setFormDiagnosis(e.target.value)} className="input-field mt-1" placeholder="Primary diagnosis" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Disease Site</label>
              <input value={formDiseaseSite} onChange={(e) => setFormDiseaseSite(e.target.value)} className="input-field mt-1" placeholder="e.g., Left cervical lymph node" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Disease Status</label>
              <input value={formDiseaseStatus} onChange={(e) => setFormDiseaseStatus(e.target.value)} className="input-field mt-1" placeholder="e.g., Newly diagnosed" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Performance Status</label>
              <CustomSelect value={formPerformanceStatus} onChange={setFormPerformanceStatus} placeholder="Select ECOG status" options={performanceStatuses.map((s) => ({ value: s, label: s }))} className="mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Treatment Intent</label>
              <CustomSelect value={formTreatmentIntent} onChange={setFormTreatmentIntent} placeholder="Select intent" options={[{ value: 'Curative', label: 'Curative' }, { value: 'Palliative', label: 'Palliative' }, { value: 'Supportive', label: 'Supportive' }]} className="mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Relapse Date</label>
              <input type="date" value={formRelapseDate} onChange={(e) => setFormRelapseDate(e.target.value)} className="input-field mt-1" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Relapse Details</label>
              <textarea value={formRelapseDetails} onChange={(e) => setFormRelapseDetails(e.target.value)} className="input-field mt-1" rows={2} placeholder="Relapse details..." />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Previous Treatment Summary</label>
              <textarea value={formPreviousTreatment} onChange={(e) => setFormPreviousTreatment(e.target.value)} className="input-field mt-1" rows={2} placeholder="Summary of previous treatments..." />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Comorbidities</label>
              <textarea value={formComorbidities} onChange={(e) => setFormComorbidities(e.target.value)} className="input-field mt-1" rows={2} placeholder="Relevant comorbidities..." />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Investigation Summary</label>
              <textarea value={formInvestigationSummary} onChange={(e) => setFormInvestigationSummary(e.target.value)} className="input-field mt-1" rows={2} placeholder="Summary of investigations..." />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <button type="button" onClick={() => { setShowAddModal(false); resetForm(); }} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Create Case</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal title="Edit MDT Case" isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedCase(null); resetForm(); }} widthClassName="max-w-4xl">
        <form onSubmit={handleUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Case Title *</label>
              <input value={formTitle} onChange={(e) => setFormTitle(e.target.value)} className="input-field mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Status</label>
              <CustomSelect value={formStatus} onChange={setFormStatus} placeholder="Select status" options={caseStatuses} className="mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Diagnosis</label>
              <input value={formDiagnosis} onChange={(e) => setFormDiagnosis(e.target.value)} className="input-field mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Disease Site</label>
              <input value={formDiseaseSite} onChange={(e) => setFormDiseaseSite(e.target.value)} className="input-field mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Disease Status</label>
              <input value={formDiseaseStatus} onChange={(e) => setFormDiseaseStatus(e.target.value)} className="input-field mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Performance Status</label>
              <CustomSelect value={formPerformanceStatus} onChange={setFormPerformanceStatus} placeholder="Select ECOG status" options={performanceStatuses.map((s) => ({ value: s, label: s }))} className="mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Treatment Intent</label>
              <CustomSelect value={formTreatmentIntent} onChange={setFormTreatmentIntent} placeholder="Select intent" options={[{ value: 'Curative', label: 'Curative' }, { value: 'Palliative', label: 'Palliative' }, { value: 'Supportive', label: 'Supportive' }]} className="mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Relapse Date</label>
              <input type="date" value={formRelapseDate} onChange={(e) => setFormRelapseDate(e.target.value)} className="input-field mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">Relapse Details</label>
              <textarea value={formRelapseDetails} onChange={(e) => setFormRelapseDetails(e.target.value)} className="input-field mt-1" rows={2} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Previous Treatment Summary</label>
              <textarea value={formPreviousTreatment} onChange={(e) => setFormPreviousTreatment(e.target.value)} className="input-field mt-1" rows={2} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Comorbidities</label>
              <textarea value={formComorbidities} onChange={(e) => setFormComorbidities(e.target.value)} className="input-field mt-1" rows={2} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">Investigation Summary</label>
              <textarea value={formInvestigationSummary} onChange={(e) => setFormInvestigationSummary(e.target.value)} className="input-field mt-1" rows={2} />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <button type="button" onClick={() => { setShowEditModal(false); setSelectedCase(null); resetForm(); }} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Save Changes</button>
          </div>
        </form>
      </Modal>

      {/* Detail Modal */}
      <Modal title="Case Details" isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedCase(null); setCompleteness(null); }} widthClassName="max-w-4xl">
        {selectedCase && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-neutral-900 mb-2">Clinical Information</h4>
                <dl className="space-y-1 text-sm">
                  <dt className="text-neutral-500">Title:</dt>
                  <dd className="text-neutral-900">{selectedCase.title}</dd>
                  <dt className="text-neutral-500">Status:</dt>
                  <dd><span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(selectedCase.status)}`}>{selectedCase.status.replace(/_/g, ' ')}</span></dd>
                  {selectedCase.diagnosis && <><dt className="text-neutral-500">Diagnosis:</dt><dd className="text-neutral-900">{selectedCase.diagnosis}</dd></>}
                  {selectedCase.disease_site && <><dt className="text-neutral-500">Disease Site:</dt><dd className="text-neutral-900">{selectedCase.disease_site}</dd></>}
                  {selectedCase.disease_status && <><dt className="text-neutral-500">Disease Status:</dt><dd className="text-neutral-900">{selectedCase.disease_status}</dd></>}
                  {selectedCase.performance_status && <><dt className="text-neutral-500">Performance Status:</dt><dd className="text-neutral-900">{selectedCase.performance_status}</dd></>}
                  {selectedCase.treatment_intent && <><dt className="text-neutral-500">Treatment Intent:</dt><dd className="text-neutral-900">{selectedCase.treatment_intent}</dd></>}
                </dl>
              </div>
              <div>
                <h4 className="font-medium text-neutral-900 mb-2">MDT Team Review Status</h4>
                <dl className="space-y-1 text-sm">
                  <div className="flex items-center space-x-2">
                    {getReviewStatusIcon(selectedCase.oncologist_review_status)}
                    <dt className="text-neutral-500">Oncologist:</dt>
                    <dd className="text-neutral-900">{selectedCase.oncologist_review_status}</dd>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getReviewStatusIcon(selectedCase.histopathologist_review_status)}
                    <dt className="text-neutral-500">Histopathologist:</dt>
                    <dd className="text-neutral-900">{selectedCase.histopathologist_review_status}</dd>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getReviewStatusIcon(selectedCase.radiologist_review_status)}
                    <dt className="text-neutral-500">Radiologist:</dt>
                    <dd className="text-neutral-900">{selectedCase.radiologist_review_status}</dd>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getReviewStatusIcon(selectedCase.supportive_care_review_status)}
                    <dt className="text-neutral-500">Supportive Care:</dt>
                    <dd className="text-neutral-900">{selectedCase.supportive_care_review_status}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {completeness && (
              <div className={`p-4 rounded-lg ${completeness.is_complete ? 'bg-success-50 border border-success-200' : 'bg-warning-50 border border-warning-200'}`}>
                <div className="flex items-center space-x-2 mb-2">
                  {completeness.is_complete ? (
                    <CheckCircle className="h-5 w-5 text-success-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-warning-600" />
                  )}
                  <h4 className={`font-medium ${completeness.is_complete ? 'text-success-800' : 'text-warning-800'}`}>
                    Case Completeness
                  </h4>
                </div>
                <p className={`text-sm ${completeness.is_complete ? 'text-success-700' : 'text-warning-700'}`}>
                  {completeness.message}
                </p>
                {!completeness.is_complete && completeness.missing_fields.length > 0 && (
                  <ul className="mt-2 text-sm text-warning-700 list-disc list-inside">
                    {completeness.missing_fields.map((field, idx) => (
                      <li key={idx}>{field}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal title="Delete Case" isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} widthClassName="max-w-md">
        <p className="text-neutral-700 mb-6">Are you sure you want to delete this MDT case? This action cannot be undone.</p>
        <div className="flex justify-end space-x-3">
          <button onClick={() => setDeleteModalOpen(false)} className="btn-secondary">Cancel</button>
          <button onClick={handleDelete} className="btn-danger">Delete</button>
        </div>
      </Modal>
    </div>
  );
};

export default CasesTab;
